import { Canvas } from "@react-three/fiber";
import './tool-card.css';
import { useState } from "react";
import * as THREE from 'three';
import { OrbitControls } from "@react-three/drei";
import { Popover } from "antd";
import { Tool } from "../../types/api/tool/get-tools-success-response";
import { getImageUrl } from "../../../../utils/image-helper/get-image-url";

type ToolCardProps = {
    tool: Tool;
    type: MenuType;
    onChoose?: () => void;
    onDelete?: () => void;
    onEdit?: () => void;
    selected?: boolean;
};

export const ToolCard = ({ tool, type, onDelete, onEdit, onChoose, selected }: ToolCardProps) => {
    const [prevToolModel, setprevToolModel] = useState<typeof tool.model>();
    const [loadedModel, setLoadedModel] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D>();

    if (prevToolModel !== tool.model) {
        setprevToolModel(tool.model);

        if (tool.model) {
            const extension = tool.model.split('.').pop()?.toLowerCase();
                        
            switch (extension) {
                case '3dm': {
                    const loadModel = async () => {
                        const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');
                        const loader = new Rhino3dmLoader();
                        loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                        loader.load(tool.model, (object) => setLoadedModel(object));
                    };
                    loadModel();
                    break;
                }
                case 'glb':
                case 'gltf': {
                    const loadModel = async () => {
                        const manager = new THREE.LoadingManager();

                        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                        const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                        const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                        const dracoLoader = new DRACOLoader();
                        dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                        const ktx2Loader = new KTX2Loader(manager);
                        ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                        const loader = new GLTFLoader(manager);
                        loader.setDRACOLoader(dracoLoader);
                        loader.setKTX2Loader(ktx2Loader);
                        loader.setMeshoptDecoder(MeshoptDecoder);

                        loader.load(tool.model, (gltf) => setLoadedModel(gltf.scene));
                    };
                    loadModel();

                    break;
                }
                case 'obj': {
                    const loadModel = async () => {
                        const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');

                        const loader = new OBJLoader();
                        
                        loader.load(tool.model, (object) => {
                            setLoadedModel(object);
                        });
                    };
                    loadModel();
                    break;
                }
                case 'drc': {
                    const loadModel = async () => {
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                        const loader = new DRACOLoader();
                        loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                        loader.load(tool.model, (geometry) => {
                            let object;

                            if ( geometry.index !== null ) {

                                const material = new THREE.MeshStandardMaterial();

                                object = new THREE.Mesh(geometry, material);
                                object.name = 'drcModell';

                            } else {

                                const material = new THREE.PointsMaterial({ size: 0.01 });
                                material.vertexColors = geometry.hasAttribute('color');

                                object = new THREE.Points(geometry, material);
                                object.name = 'drcModell';

                            }

                            setLoadedModel(object);
                        });
                    };
                    loadModel();
                    
                    break;
                }
                case 'fbx': {
                    const loadModel = async () => {
                        const manager = new THREE.LoadingManager();
                        const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                        const loader = new FBXLoader(manager);

                        loader.load(tool.model, (object) => setLoadedModel(object));
                    };
                    loadModel();

                    break;
                }
                //case 'max':
                case '3ds': {
                    const loadModel = async () => {
                        const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                        const loader = new TDSLoader();

                        loader.load(tool.model, (object) => setLoadedModel(object));
                    };
                    loadModel();

                    break;
                }
            }
        }
    }

    return (
        <div className={`tool-card box-shadow-4-4-8-0 ${ selected === true ? 'selected' : ''}`}>
            <div className="header">
                { tool.name }
                <Popover
                    content={<ToolCardMenu type={type} onDelete={onDelete} onChoose={onChoose} onEdit={onEdit} />}
                    trigger="click"
                >
                    <img className="cursor-pointer" src={getImageUrl('file-menu-icon.png')} alt="menu-icon" />
                </Popover>
            </div>
            <Canvas camera={{ position: [2, 2, 8] }}>
                { loadedModel && <primitive object={loadedModel} /> }
                <OrbitControls makeDefault />
                <ambientLight />
            </Canvas>
        </div>
    );
};

type MenuType = 'chooser' | 'edit';

type ToolCardMenuProps = {
    type: MenuType;
    onChoose?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
};

const ToolCardMenu = ({ type, onChoose, onDelete, onEdit }: ToolCardMenuProps) => {
    if (type === 'chooser') {
        const handleChooseClick = () => {
            if (onChoose) onChoose();
        };

        return (
            <ul className="tool-card-popover-menu">
                <li onClick={handleChooseClick}>Választ</li>
            </ul>
        );
    }

    const handleEditClick = () => {
        if (onEdit) onEdit();
    };

    const handleDeleteClick = () => {
        if (onDelete) onDelete();
    };

    return (
        <ul className="tool-card-popover-menu">
            <li onClick={handleEditClick}>Módosítás</li>
            <li onClick={handleDeleteClick}>Törlés</li>
        </ul>
    );
};